import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  addTrackerHost,
  listTrackerHosts,
  removeTrackerHost,
} from '../../../services/tracker-api-client';
import { hostFailureMessage, isOwner, sortHosts } from '../../../lib/groupHosts';
import type { TrackerHost } from '../../../services/tracker-api-client';
import type { GroupDetail } from '../../../types/groups';

/**
 * Who else can manage this trip.
 *
 * The endpoints have existed since the tracker shipped, and the board already
 * READS hosts — their names are what signs an invite email. What was missing
 * was any way to change the list: a second host had to be added by someone with
 * database access, on a product whose premise is that a couple plans the
 * weekend together. One person held the whole trip, and if they were on a plane
 * nobody else could send a broadcast or fix a dinner.
 *
 * Adding and removing are owner-only, server-side. A host who is not the owner
 * sees the list and no controls — which is the point of the gate: a co-host
 * cannot quietly widen the circle that reads guests' phone numbers, home
 * airports and confirmation numbers.
 */
export default function HostsCard({ group }: { group: GroupDetail }) {
  const queryClient = useQueryClient();
  const key = ['group-hosts', group.id];
  const hostsQuery = useQuery(key, () => listTrackerHosts(group.id));

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');

  const refresh = () => queryClient.invalidateQueries(key);
  const addMutation = useMutation(() => addTrackerHost(group.id, email, displayName), {
    onSuccess: (result) => {
      if (!result.ok) return;
      setEmail('');
      setDisplayName('');
      refresh();
    },
  });
  const removeMutation = useMutation((hostId: string) => removeTrackerHost(group.id, hostId), {
    onSuccess: (result) => { if (result.ok) refresh(); },
  });

  const owner = isOwner(group.viewerRole);
  const busy = addMutation.isLoading || removeMutation.isLoading;
  /*
    The failure the API returned, not the one axios threw. These endpoints
    answer 404 for "you are not a host on this trip" so they cannot be used to
    confirm which group ids exist, and 403 with a sentence for an owner-only
    action — both arrive as resolved results, not rejections.
  */
  const failure = hostFailureMessage([
    hostsQuery.data,
    addMutation.data,
    removeMutation.data,
  ]);
  const hosts = hostsQuery.data?.ok ? sortHosts(hostsQuery.data.data) : [];

  return (
    <Paper sx={{ p: 3, borderRadius: 3, gridColumn: { md: '1 / -1' } }}>
      <Typography variant="h6" fontWeight={800}>Hosts</Typography>
      <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
        Everyone here can see the guest list and send on behalf of this trip. Their names
        sign the invitations.
      </Typography>

      {failure ? <Alert severity="warning" sx={{ mt: 2 }}>{failure}</Alert> : null}

      {hostsQuery.isLoading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading hosts…</Typography>
        </Stack>
      ) : (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {hosts.map((host: TrackerHost) => (
            <Stack
              key={host.id}
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ justifyContent: 'space-between' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {host.displayName || host.email}
                </Typography>
                {/* The email is the identity the server matches on, so it is
                    always shown — a display name alone cannot be checked against
                    the address someone actually signs in with. */}
                <Typography variant="caption" color="text.secondary" noWrap>
                  {host.email}
                </Typography>
              </Box>
              {owner ? (
                <IconButton
                  size="small"
                  disabled={busy}
                  aria-label={`Remove ${host.displayName || host.email}`}
                  onClick={() => {
                    if (!window.confirm(
                      `Remove ${host.displayName || host.email}? They lose access to the guest list, the dinners and the broadcasts.`,
                    )) return;
                    removeMutation.mutate(host.id);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Stack>
          ))}
          {hosts.length === 0 && !failure ? (
            <Typography variant="body2" color="text.secondary">
              Just you so far.
            </Typography>
          ) : null}
        </Stack>
      )}

      {owner ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
          <TextField
            size="small"
            label="Email"
            type="email"
            value={email}
            disabled={busy}
            onChange={(event) => setEmail(event.target.value)}
            helperText="The address they sign in with."
            sx={{ flexGrow: 1 }}
          />
          <TextField
            size="small"
            label="Name (optional)"
            value={displayName}
            disabled={busy}
            onChange={(event) => setDisplayName(event.target.value)}
            helperText="How they are named on invitations."
            sx={{ flexGrow: 1 }}
          />
          <Box>
            <Button
              variant="outlined"
              disabled={busy || !email.trim()}
              onClick={() => { addMutation.reset(); addMutation.mutate(); }}
            >
              {addMutation.isLoading ? 'Adding…' : 'Add host'}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Chip size="small" variant="outlined" label="Only the trip's owner can change this" sx={{ mt: 2 }} />
      )}
    </Paper>
  );
}
